/**
 * Brand Profiles Page - Agency Feature
 *
 * Allows agency users to create and manage multiple brand profiles.
 * Each profile defines brand voice, tone, keywords, and topics to avoid.
 *
 * Features:
 * - List all brand profiles
 * - Create new profile
 * - Edit existing profile
 * - Delete profile
 * - Set default profile
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Star,
  StarOff,
  Building2,
  Briefcase,
  Users,
  Tag,
  X,
  Check,
  Loader2,
  AlertCircle,
  Upload,
  Image as ImageIcon,
  Palette,
} from 'lucide-react';

// Brand Profile interface matching backend
interface BrandProfile {
  id: string;
  name: string;
  description?: string;
  industry?: string;
  tone: string;
  style?: string;
  target_audience?: string;
  keywords: string[];
  avoid_topics: string[];
  created_at: string;
  updated_at?: string;
  // Branding fields (Agency feature)
  logo_url?: string;
  custom_footer_text?: string;
  brand_colors?: {
    primary?: string;
    secondary?: string;
  };
}

// Form data for create/edit
interface ProfileFormData {
  name: string;
  description: string;
  industry: string;
  tone: string;
  style: string;
  target_audience: string;
  keywords: string[];
  avoid_topics: string[];
  // Branding fields
  logo_url: string;
  custom_footer_text: string;
  brand_colors: {
    primary: string;
    secondary: string;
  };
}

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'authoritative', label: 'Authoritative' },
  { value: 'conversational', label: 'Conversational' },
  { value: 'formal', label: 'Formal' },
  { value: 'witty', label: 'Witty' },
  { value: 'empathetic', label: 'Empathetic' },
];

const INDUSTRY_OPTIONS = [
  'Technology',
  'Healthcare',
  'Finance',
  'E-commerce',
  'Education',
  'Real Estate',
  'Marketing',
  'Legal',
  'Manufacturing',
  'Hospitality',
  'Other',
];

interface BrandProfilesPageProps {
  onBack: () => void;
  getAuthHeaders?: () => Promise<Record<string, string>>;
  apiBase?: string;
}

export const BrandProfilesPage: React.FC<BrandProfilesPageProps> = ({
  onBack,
  getAuthHeaders,
  apiBase = '',
}) => {
  const [profiles, setProfiles] = useState<BrandProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [defaultProfileId, setDefaultProfileId] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState<BrandProfile | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    name: '',
    description: '',
    industry: '',
    tone: 'professional',
    style: '',
    target_audience: '',
    keywords: [],
    avoid_topics: [],
    logo_url: '',
    custom_footer_text: '',
    brand_colors: { primary: '#6366f1', secondary: '#8b5cf6' },
  });
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [keywordInput, setKeywordInput] = useState('');
  const [avoidTopicInput, setAvoidTopicInput] = useState('');

  // Load profiles on mount
  useEffect(() => {
    loadProfiles();
    // Load default profile from localStorage
    const savedDefault = localStorage.getItem('defaultBrandProfileId');
    if (savedDefault) setDefaultProfileId(savedDefault);
  }, []);

  const loadProfiles = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const headers = getAuthHeaders ? await getAuthHeaders() : {};
      const response = await fetch(`${apiBase}/api/v1/blog/brand-profiles`, {
        headers: {
          ...headers,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProfiles(data.profiles || []);
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Failed to load profiles');
      }
    } catch (err) {
      setError('Network error loading profiles');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingProfile(null);
    setFormData({
      name: '',
      description: '',
      industry: '',
      tone: 'professional',
      style: '',
      target_audience: '',
      keywords: [],
      avoid_topics: [],
      logo_url: '',
      custom_footer_text: '',
      brand_colors: { primary: '#6366f1', secondary: '#8b5cf6' },
    });
    setLogoPreview(null);
    setShowForm(true);
  };

  const handleEdit = (profile: BrandProfile) => {
    setEditingProfile(profile);
    setFormData({
      name: profile.name,
      description: profile.description || '',
      industry: profile.industry || '',
      tone: profile.tone,
      style: profile.style || '',
      target_audience: profile.target_audience || '',
      keywords: profile.keywords || [],
      avoid_topics: profile.avoid_topics || [],
      logo_url: profile.logo_url || '',
      custom_footer_text: profile.custom_footer_text || '',
      brand_colors: {
        primary: profile.brand_colors?.primary || '#6366f1',
        secondary: profile.brand_colors?.secondary || '#8b5cf6',
      },
    });
    setLogoPreview(profile.logo_url || null);
    setShowForm(true);
  };

  // Handle logo file upload
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a PNG, JPG, or SVG file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Logo file must be less than 2MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setLogoPreview(dataUrl);
      // Store as data URL for now (in production, upload to S3)
      setFormData(prev => ({ ...prev, logo_url: dataUrl }));
    };
    reader.readAsDataURL(file);
  };

  const clearLogo = () => {
    setLogoPreview(null);
    setFormData(prev => ({ ...prev, logo_url: '' }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Profile name is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const headers = getAuthHeaders ? await getAuthHeaders() : {};
      const url = editingProfile
        ? `${apiBase}/api/v1/blog/brand-profiles/${editingProfile.id}`
        : `${apiBase}/api/v1/blog/brand-profiles`;
      const method = editingProfile ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        await loadProfiles();
        setShowForm(false);
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Failed to save profile');
      }
    } catch (err) {
      setError('Network error saving profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (profileId: string) => {
    if (!confirm('Are you sure you want to delete this profile?')) return;

    try {
      const headers = getAuthHeaders ? await getAuthHeaders() : {};
      const response = await fetch(`${apiBase}/api/v1/blog/brand-profiles/${profileId}`, {
        method: 'DELETE',
        headers: {
          ...headers,
        },
      });

      if (response.ok) {
        await loadProfiles();
        if (defaultProfileId === profileId) {
          setDefaultProfileId(null);
          localStorage.removeItem('defaultBrandProfileId');
        }
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Failed to delete profile');
      }
    } catch (err) {
      setError('Network error deleting profile');
    }
  };

  const handleSetDefault = (profileId: string) => {
    if (defaultProfileId === profileId) {
      setDefaultProfileId(null);
      localStorage.removeItem('defaultBrandProfileId');
    } else {
      setDefaultProfileId(profileId);
      localStorage.setItem('defaultBrandProfileId', profileId);
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()],
      }));
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword),
    }));
  };

  const addAvoidTopic = () => {
    if (avoidTopicInput.trim() && !formData.avoid_topics.includes(avoidTopicInput.trim())) {
      setFormData(prev => ({
        ...prev,
        avoid_topics: [...prev.avoid_topics, avoidTopicInput.trim()],
      }));
      setAvoidTopicInput('');
    }
  };

  const removeAvoidTopic = (topic: string) => {
    setFormData(prev => ({
      ...prev,
      avoid_topics: prev.avoid_topics.filter(t => t !== topic),
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={onBack}
            className="flex items-center text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
          <h1 className="text-lg font-semibold text-white">Brand Profiles</h1>
          <button
            onClick={handleCreateNew}
            className="p-2 bg-purple-600 rounded-lg hover:bg-purple-500 transition-colors"
          >
            <Plus className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Error message */}
        {error && (
          <div className="flex items-center p-3 bg-red-900/30 border border-red-700/50 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            <span className="text-red-300 text-sm">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-300"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && profiles.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No brand profiles yet</h3>
            <p className="text-slate-400 mb-6">
              Create your first brand profile to customize content generation
            </p>
            <button
              onClick={handleCreateNew}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors inline-flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Profile
            </button>
          </div>
        )}

        {/* Profile list */}
        {!isLoading && profiles.length > 0 && (
          <div className="space-y-3">
            {profiles.map((profile) => (
              <motion.div
                key={profile.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-xl border transition-colors ${
                  defaultProfileId === profile.id
                    ? 'bg-purple-900/30 border-purple-600'
                    : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    {defaultProfileId === profile.id && (
                      <Star className="w-4 h-4 text-amber-400 mr-2 fill-amber-400" />
                    )}
                    <h3 className="font-medium text-white">{profile.name}</h3>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleSetDefault(profile.id)}
                      className="p-1.5 text-slate-400 hover:text-amber-400 transition-colors"
                      title={defaultProfileId === profile.id ? 'Remove default' : 'Set as default'}
                    >
                      {defaultProfileId === profile.id ? (
                        <StarOff className="w-4 h-4" />
                      ) : (
                        <Star className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEdit(profile)}
                      className="p-1.5 text-slate-400 hover:text-purple-400 transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(profile.id)}
                      className="p-1.5 text-slate-400 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {profile.description && (
                  <p className="text-sm text-slate-400 mb-2">{profile.description}</p>
                )}

                <div className="flex flex-wrap gap-2 text-xs">
                  {profile.industry && (
                    <span className="px-2 py-1 bg-slate-700/50 text-slate-300 rounded-full flex items-center">
                      <Briefcase className="w-3 h-3 mr-1" />
                      {profile.industry}
                    </span>
                  )}
                  <span className="px-2 py-1 bg-purple-900/30 text-purple-300 rounded-full">
                    {profile.tone}
                  </span>
                  {profile.target_audience && (
                    <span className="px-2 py-1 bg-slate-700/50 text-slate-300 rounded-full flex items-center">
                      <Users className="w-3 h-3 mr-1" />
                      {profile.target_audience}
                    </span>
                  )}
                </div>

                {profile.keywords.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {profile.keywords.slice(0, 3).map((keyword) => (
                      <span
                        key={keyword}
                        className="px-2 py-0.5 bg-slate-700/30 text-slate-400 text-xs rounded"
                      >
                        {keyword}
                      </span>
                    ))}
                    {profile.keywords.length > 3 && (
                      <span className="px-2 py-0.5 text-slate-500 text-xs">
                        +{profile.keywords.length - 3} more
                      </span>
                    )}
                  </div>
                )}

                {/* White-label branding indicator */}
                {(profile.logo_url || profile.custom_footer_text) && (
                  <div className="mt-3 pt-2 border-t border-slate-700/50 flex items-center gap-2">
                    {profile.logo_url && (
                      <div className="w-6 h-6 bg-slate-700 rounded overflow-hidden">
                        <img
                          src={profile.logo_url}
                          alt="Brand logo"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                    <span className="text-xs text-purple-400 flex items-center">
                      <Palette className="w-3 h-3 mr-1" />
                      White-label enabled
                    </span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-end justify-center"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="w-full max-h-[90vh] bg-slate-900 rounded-t-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Form Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                <h2 className="text-lg font-semibold text-white">
                  {editingProfile ? 'Edit Profile' : 'New Profile'}
                </h2>
                <button
                  onClick={() => setShowForm(false)}
                  className="p-2 text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form Content */}
              <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Profile Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Tech Blog Voice"
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of this brand profile..."
                    rows={2}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Industry */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Industry
                  </label>
                  <select
                    value={formData.industry}
                    onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select industry...</option>
                    {INDUSTRY_OPTIONS.map((industry) => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>

                {/* Tone */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Writing Tone
                  </label>
                  <select
                    value={formData.tone}
                    onChange={(e) => setFormData(prev => ({ ...prev, tone: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {TONE_OPTIONS.map((tone) => (
                      <option key={tone.value} value={tone.value}>{tone.label}</option>
                    ))}
                  </select>
                </div>

                {/* Target Audience */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Target Audience
                  </label>
                  <input
                    type="text"
                    value={formData.target_audience}
                    onChange={(e) => setFormData(prev => ({ ...prev, target_audience: e.target.value }))}
                    placeholder="e.g., Small business owners"
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Keywords */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Brand Keywords
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                      placeholder="Add keyword..."
                      className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <button
                      onClick={addKeyword}
                      className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.keywords.map((keyword) => (
                      <span
                        key={keyword}
                        className="px-2 py-1 bg-purple-900/30 text-purple-300 text-sm rounded-full flex items-center"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {keyword}
                        <button
                          onClick={() => removeKeyword(keyword)}
                          className="ml-1 hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Topics to Avoid */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Topics to Avoid
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={avoidTopicInput}
                      onChange={(e) => setAvoidTopicInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAvoidTopic())}
                      placeholder="Add topic to avoid..."
                      className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <button
                      onClick={addAvoidTopic}
                      className="px-3 py-2 bg-red-600/50 text-white rounded-lg hover:bg-red-600"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.avoid_topics.map((topic) => (
                      <span
                        key={topic}
                        className="px-2 py-1 bg-red-900/30 text-red-300 text-sm rounded-full flex items-center"
                      >
                        {topic}
                        <button
                          onClick={() => removeAvoidTopic(topic)}
                          className="ml-1 hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Branding Settings Section - White-Label Feature */}
                <div className="pt-4 border-t border-slate-700">
                  <div className="flex items-center gap-2 mb-4">
                    <Palette className="w-5 h-5 text-purple-400" />
                    <h3 className="text-sm font-semibold text-white">White-Label Branding</h3>
                    <span className="text-[10px] px-1.5 py-0.5 bg-purple-900/50 text-purple-300 rounded-full">
                      AGENCY
                    </span>
                  </div>

                  {/* Logo Upload */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Brand Logo
                    </label>
                    <div className="flex items-start gap-4">
                      {/* Logo Preview */}
                      <div className="w-20 h-20 bg-slate-700/50 rounded-lg border-2 border-dashed border-slate-600 flex items-center justify-center overflow-hidden">
                        {logoPreview ? (
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <ImageIcon className="w-8 h-8 text-slate-500" />
                        )}
                      </div>
                      {/* Upload Controls */}
                      <div className="flex-1 space-y-2">
                        <label className="flex items-center justify-center px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg cursor-pointer transition-colors">
                          <Upload className="w-4 h-4 mr-2" />
                          <span className="text-sm">Upload Logo</span>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                        </label>
                        {logoPreview && (
                          <button
                            onClick={clearLogo}
                            className="w-full px-3 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-300 rounded-lg text-sm transition-colors"
                          >
                            Remove Logo
                          </button>
                        )}
                        <p className="text-xs text-slate-500">
                          PNG, JPG, or SVG. Max 2MB.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Custom Footer Text */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Custom Footer Text
                    </label>
                    <input
                      type="text"
                      value={formData.custom_footer_text}
                      onChange={(e) => setFormData(prev => ({ ...prev, custom_footer_text: e.target.value }))}
                      placeholder="e.g., Generated by Your Agency Name"
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Replaces "ContentSage" in exports
                    </p>
                  </div>

                  {/* Brand Colors */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Brand Colors
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Primary</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={formData.brand_colors.primary}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              brand_colors: { ...prev.brand_colors, primary: e.target.value }
                            }))}
                            className="w-10 h-10 rounded-lg cursor-pointer border-0 bg-transparent"
                          />
                          <input
                            type="text"
                            value={formData.brand_colors.primary}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              brand_colors: { ...prev.brand_colors, primary: e.target.value }
                            }))}
                            className="flex-1 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-sm text-white font-mono"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Secondary</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={formData.brand_colors.secondary}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              brand_colors: { ...prev.brand_colors, secondary: e.target.value }
                            }))}
                            className="w-10 h-10 rounded-lg cursor-pointer border-0 bg-transparent"
                          />
                          <input
                            type="text"
                            value={formData.brand_colors.secondary}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              brand_colors: { ...prev.brand_colors, secondary: e.target.value }
                            }))}
                            className="flex-1 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-sm text-white font-mono"
                          />
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Used in PDF headers and styling
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 px-4 py-4 border-t border-slate-800">
                <button
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving || !formData.name.trim()}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                >
                  {isSaving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      {editingProfile ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BrandProfilesPage;
